import React, { useEffect, useMemo, useRef, useState } from 'react';
import { m as motion } from 'framer-motion';
import cn from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import { Link } from 'gatsby';

import { GOALS, trackGoal } from '../utils/fathom';
import { avoidWidows } from '../utils/typography';
import { useSteppedTransition } from '../hooks/useSteppedTransition';
import useTime from '../hooks/useTime';

import Logo from './Logo';
import ContactForm from './ContactForm/ContactForm';

import info from '../../content/info.json';

import * as css from './Footer.module.css';

const FooterCity = ({ city, timezone, url }) => {
  const time = useTime(timezone);

  return (
    <div className={cn(css.city, css.columnItem)}>
      <div className={css.cityName}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          aria-label={`A fun fact about ${city}`}
          onClick={() => trackGoal(GOALS.CLICK_INSTAGRAM_LINK)}
          className={css.contactLink}>
          {city}
        </a>
      </div>
      <div className={css.cityTime}>{time}</div>
    </div>
  );
};

const Footer = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const openContactForm = (e) => {
    e.preventDefault();
    setShowContactForm(true);
  };
  const closeConctactForm = () => setShowContactForm(false);

  const transition = useSteppedTransition({
    duration: 0.5,
    steps: 15
  });

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          email
          citiesWithTimezones {
            city
            timezone
            url
          }
          founded
          menu {
            label
            longLabel
            path
          }
          twitterHandle
          githubHandle
          instagramHandle
          linkedinHandle

          address {
            street
            city
            country
          }
        }
      }
    }
  `);

  const {
    email,
    citiesWithTimezones,
    founded,
    twitterHandle,
    githubHandle,
    instagramHandle,
    linkedinHandle
  } = site.siteMetadata;

  const daysFromFoundation = Math.floor(
    (Date.now() - new Date(founded).valueOf()) / 1000 / 60 / 60 / 24
  );

  const facts = useMemo(
    () => [
      `was founded ${daysFromFoundation} days ago.`,
      `has worked with ${info.numClients} clients worldwide.`,
      `has delivered ${info.numProjects} projects, and counting.`
    ],
    [daysFromFoundation, info]
  );

  const timer = useRef();
  const [fact, setFact] = useState(0);

  const nextFact = () => {
    setFact((f) => (f + 1) % facts.length);
    clearTimeout(timer.current);
    timer.current = setTimeout(nextFact, 3000);
  };
  useEffect(() => {
    nextFact();
    return () => clearInterval(timer.current);
  }, [facts]);

  return (
    <footer className={css.root}>
      <div className={css.fact} onClick={nextFact}>
        <span className={css.factIntro}>Design Systems International</span>{' '}
        <span className={css.factHightlight}>{avoidWidows(facts[fact])}</span>
      </div>

      <div className={css.contactFormParent}>
        <div className={css.contact}>
          <div className={cn(css.column, css.whereWeAre)}>
            <h3 className={css.columnTitle}>Where We Are</h3>
            {citiesWithTimezones.map(({ city, timezone, url }, idx) => (
              <FooterCity key={idx} city={city} timezone={timezone} url={url} />
            ))}
          </div>

          <div className={css.column}>
            <h3 className={css.columnTitle}>Follow Us</h3>

            <div className={css.contactLinkList}>
              <a
                href={`https://www.instagram.com/${instagramHandle}/`}
                target="_blank"
                rel="noreferrer"
                aria-label="Design Systems International on Instagram"
                onClick={() => trackGoal(GOALS.CLICK_INSTAGRAM_LINK)}
                className={css.contactLink}>
                Instagram
              </a>
              <a
                href={`https://www.twitter.com/${twitterHandle}/`}
                target="_blank"
                rel="noreferrer"
                aria-label="Design Systems International on X"
                onClick={() => trackGoal(GOALS.CLICK_TWITTER_LINK)}
                className={css.contactLink}>
                Twitter
              </a>
              <a
                href={`https://www.github.com/${githubHandle}/`}
                target="_blank"
                rel="noreferrer"
                aria-label="Design Systems International on GitHub"
                onClick={() => trackGoal(GOALS.CLICK_GITHUB_LINK)}
                className={css.contactLink}>
                GitHub
              </a>
              <a
                href={`https://www.linkedin.com/company/${linkedinHandle}/`}
                target="_blank"
                rel="noreferrer"
                aria-label="Design Systems International on LinkedIn"
                onClick={() => trackGoal(GOALS.CLICK_LINKEDIN_LINK)}
                className={css.contactLink}>
                LinkedIn
              </a>
            </div>
          </div>

          <div className={css.column}>
            <h3 className={css.columnTitle}>Menu</h3>
            <ul className={css.menu}>
              <Link
                className={css.contactLink}
                to={'/'}
                onClick={() => trackGoal(GOALS.CLICK_FOOTER_MENU_ITEM)}>
                Home
              </Link>
              {site.siteMetadata.menu.map((item) => (
                <li key={item.path} className={css.columnItem}>
                  <Link
                    className={css.contactLink}
                    to={item.path}
                    onClick={() => trackGoal(GOALS.CLICK_FOOTER_MENU_ITEM)}>
                    {item.longLabel || item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={cn(css.column, css.contactColumn)}>
            <h3 className={css.columnTitle}>Contact Us</h3>
            <div className={css.contactUs}>
              <p>
                You can get in contact with us via email at:
                <br />
                <br />
                <a
                  href={`mailto:Design Systems International<${email}>?subject=Contact from Website`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackGoal(GOALS.CLICK_MAILTO_LINK)}
                  className={css.contactLink}>
                  {email}
                </a>
                <br />
                <br />
                or by using this mighty{' '}
                <a
                  href="#"
                  onClick={openContactForm}
                  className={css.contactLink}>
                  form
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        <div className={css.bottomBar}>
          <Link className={css.bottomLogo} to="/">
            <Logo />
          </Link>
          <div className={css.bottomCopy}>
            &copy; {new Date().getFullYear()} Design Systems International
          </div>
        </div>

        <motion.div
          className={css.contactFormWrapper}
          variants={{
            closed: {
              opacity: 0,
              pointerEvents: 'none',
              y: '100%'
            },
            visible: {
              opacity: 1,
              pointerEvents: 'all',
              y: 0
            }
          }}
          animate={showContactForm ? 'visible' : 'closed'}
          transition={transition}>
          <ContactForm
            shown={showContactForm}
            onCloseIntent={closeConctactForm}
          />
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
